import { Form, Input } from 'antd5';
import moment from 'moment';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { convertToAssetHierarchyItem } from 'components/choosers/helpers';
import { TaskAssetPicker } from 'components/choosers/TaskAssetPicker';
import { VendorOrgChooser } from 'components/choosers/VendorOrgChooser';
import { Button, ModalActionButtons } from 'components/ui';
import { UTCDatePicker } from 'components/UTCDatePicker';
import { AssetPicker } from 'horizon/components/Assets/AssetPicker';
import { type AssetHierarchyItem } from 'horizon/components/Assets/AssetPicker/types';
import {
  type AtlasGqlAsset,
  type AtlasGqlInspection,
  type AtlasGqlInspectionTask,
  type AtlasGqlInternalBladeInspectionTask,
  AtlasGqlInspectionSiteType,
  AtlasGqlWorkContainerTypeOption,
  useCreateInspectionMutation,
  useCreateTaskMutation,
  useGetInspectionTypesQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';
import { ASSET_TYPE_SITE, INSPECTION_TASK } from 'utils/constants';
import { useFeatures } from 'utils/features';
import { BOP_INSPECTIONS, INSPECTION_VENDOR_NAMES } from 'utils/release-toggles';

import { InspectionTaskChooser } from '../InspectionTaskChooser';
import { InspectionTypeChooser } from '../InspectionTypeChooser';
import { InspectionVendorChooser } from '../InspectionVendorChooser';

import {
  getCreateInspectionVendorInput,
  getVendorId,
  useFilterAssetTypeOptionsForInspectionUploads,
} from './util';

// The values passed to the API (and come from the DB)
// are UUIDs of the types and vendors
const groundPhotoTypeIds = [
  '91346791-4cd6-4841-b9ac-414629ed4298', // "Ground Photo" inspection type in dev
  '2039dfa8-4da3-11ea-94a2-8394dac9d621', // "Ground Photo" inspection type in prod
];

interface Props {
  onClose: () => void;
  onCreate?: (inspection: AtlasGqlInspection | undefined) => void | Promise<void>;
  setHasNewInspectionTask?: Dispatch<SetStateAction<boolean>>;
  task?: (AtlasGqlInspectionTask | AtlasGqlInternalBladeInspectionTask) | null;
  asset?: AtlasGqlAsset | null;
  saveButtonText?: string;
}

export const CreateSingleInspectionForm = ({
  onClose,
  onCreate,
  setHasNewInspectionTask,
  task,
  asset,
  saveButtonText,
}: Props) => {
  const { t } = useTranslation();
  const { hasReleaseToggle } = useAccountContext();
  const hasBopInspections = hasReleaseToggle(BOP_INSPECTIONS);
  const hasVendorNames = hasReleaseToggle(INSPECTION_VENDOR_NAMES);

  const { INSPECTION_UPLOAD, WORK_ORDERS, MODIFY_SKYSPECS_INSPECTION } = useFeatures().features;

  const [currentAsset, setCurrentAsset] = useState<{
    assetId?: string;
    assetTypeId?: string;
    assetTypeName?: string;
    siteType?: AtlasGqlInspectionSiteType;
  }>({
    assetId: undefined,
    assetTypeId: undefined,
    assetTypeName: undefined,
    siteType: undefined,
  });

  // By default, the vendor selection should be disabled, unless the user has access to the SkySpecs vendor option as well as Third Party
  const [vendorDisabled, setVendorDisabled] = useState(MODIFY_SKYSPECS_INSPECTION ? false : true);
  const [inspectionTypeId, setInspectionTypeId] = useState<string>();
  const [isChoosingAssetType, setIsChoosingAssetType] = useState(false);

  const [form] = Form.useForm();

  const filterAssetTypeOption = useFilterAssetTypeOptionsForInspectionUploads();

  // create task mutation
  const [createTask, { loading: createTaskLoading }] = useCreateTaskMutation();

  // create inspection mutation
  const [createInspection, { loading: createInspectionLoading }] = useCreateInspectionMutation();

  const isInspectionTask = task?.type?.type === INSPECTION_TASK;

  const isSite = currentAsset.assetTypeName === ASSET_TYPE_SITE;
  const isSolarSite = isSite && currentAsset.siteType === AtlasGqlInspectionSiteType.Solar;
  const shouldUseBackendSiteInspectionTypes =
    hasBopInspections && isSite && Boolean(currentAsset.assetTypeId);
  const { data: siteInspectionTypeData, loading: siteInspectionTypesLoading } =
    useGetInspectionTypesQuery({
      variables: {
        siteType: currentAsset.siteType,
        filterBy: currentAsset.assetTypeId
          ? [{ key: 'assetTypeId', values: [currentAsset.assetTypeId] }]
          : [],
      },
      skip: !shouldUseBackendSiteInspectionTypes,
    });
  const hasValidSiteInspectionTypes = (siteInspectionTypeData?.inspectionTypes?.length ?? 0) > 0;
  const isCheckingSiteInspectionTypes =
    shouldUseBackendSiteInspectionTypes && siteInspectionTypesLoading;
  const canTargetSite = isSite && (hasBopInspections ? hasValidSiteInspectionTypes : isSolarSite);

  const onTypeChange = (value: string) => {
    setInspectionTypeId(value);
    // If the selected inspection type is "Ground Photo", set the flag to true, and enable the vendor dropdown
    // As there will always be at least two options for selection: "Third Party" and "Cornis"
    if (groundPhotoTypeIds.includes(value)) {
      setVendorDisabled(false);
      return;
    }
    // Check to see if the user has access to the SkySpecs vendor
    if (MODIFY_SKYSPECS_INSPECTION) {
      // If the user has this feature flag, always set vendorDisabled to false, as there will always be at least two valid vendor options of Third Party and Skyspecs
      setVendorDisabled(false);
    } else {
      // If the feature flag is not present, disable the vendor dropdown and hardcode the vendor the "Third Party"
      setVendorDisabled(true);
      // TG: I don't like using the process.env check here but not sure how else we can do this
      // without the BE just providing us the right ID's to use.
      form.setFieldsValue({ vendorId: getVendorId(process.env['DEPLOY_ENV'] !== 'prod') });
    }
  };

  const onAssetChange = (assetInfo?: AssetHierarchyItem) => {
    const assetId = assetInfo?.assetId;
    const assetTypeId = assetInfo?.assetTypeId;
    const assetTypeName = assetInfo?.assetTypeName;
    const siteType = assetInfo?.siteType;

    // Asset change should reset the type and task selection
    form.setFieldsValue({ typeId: null });
    form.setFieldsValue({ selectedTaskId: undefined });
    setInspectionTypeId(undefined);
    setIsChoosingAssetType(false);
    setVendorDisabled(MODIFY_SKYSPECS_INSPECTION ? false : true);

    if (assetId && assetTypeId && assetTypeName && siteType) {
      setCurrentAsset({
        assetId,
        assetTypeId,
        assetTypeName,
        siteType,
      });
    }

    // site selections should invalidate asset form
    // value selection every time. A user needs to
    // select a more specific asset.
    if (!assetId || !assetTypeId || !assetTypeName || !siteType) {
      form.setFieldsValue({ assetData: null });
      setCurrentAsset({
        assetId: undefined,
        assetTypeId: undefined,
        assetTypeName: undefined,
        siteType: undefined,
      });
    }
  };

  const onAssetTypeChange = (assetTypeId?: string) => {
    form.setFieldsValue({ typeId: null, selectedTaskId: undefined });
    setInspectionTypeId(undefined);
    setVendorDisabled(MODIFY_SKYSPECS_INSPECTION ? false : true);
    setIsChoosingAssetType(Boolean(assetTypeId));
  };

  const onTaskChange = (value?: string | null, forOrganization?: string | null) => {
    /**
     * if value === null, user has elected to create a new task for the inspection
     * if undefined, they are in the process of choosing or will continue without selecting task
     * if string, an existing task has been selected
     */
    if (setHasNewInspectionTask) {
      setHasNewInspectionTask(value === null);
    }
    form.setFieldsValue({
      selectedTaskId: value,
      forOrganization,
    });
  };

  const handleSave = async ({
    inspectionDate,
    ...values
  }: {
    assetData: any;
    inspectionDate: string;
    description?: string;
    typeId: string;
    vendorId: string;
    vendorOrganizationId?: string;
    selectedTaskId?: string | null;
    forOrganization?: string | null;
  }) => {
    const formattedDate = moment(inspectionDate).format('YYYY-MM-DD');
    const { description, typeId, vendorId, vendorOrganizationId, selectedTaskId, forOrganization } =
      values;
    const { assetId } = values?.assetData;
    const shouldNotSkipTaskCreation = selectedTaskId === null;

    let taskId: string | undefined;
    if (task) {
      taskId = task.id;
    } else {
      // conditionally create task
      if (selectedTaskId) {
        // use selected inspection task
        taskId = selectedTaskId;
      } else if (shouldNotSkipTaskCreation) {
        // create new task
        const taskResult = await createTask({
          variables: {
            input: {
              type: AtlasGqlWorkContainerTypeOption.InspectionTask,
              assetId,
              description,
              ...(forOrganization ? { forOrganization } : {}),
            },
          },
        });
        taskId = taskResult.data?.createTask?.id;
      }
    }

    // create inspection
    const inspectionResult = await createInspection({
      variables: {
        input: {
          ...(canTargetSite ? { siteId: assetId } : {}),
          assetId,
          description,
          inspectionDate: formattedDate,
          typeId,
          ...getCreateInspectionVendorInput({
            hasVendorNames,
            vendorId,
            vendorOrganizationId,
            isNotProd: process.env['DEPLOY_ENV'] !== 'prod',
          }),
          taskId,
        },
      },
    });
    const newInspection = inspectionResult.data?.createInspection?.inspection as
      | AtlasGqlInspection
      | undefined;

    if (onCreate) {
      await onCreate(newInspection);
    }
  };

  return (
    <>
      <Form layout="vertical" form={form} onFinish={handleSave}>
        <Form.Item
          label={t('table.asset', 'Asset')}
          name="assetData"
          rules={[
            {
              required: !isInspectionTask,
              message: t('inspection.chooseAnAsset', 'Please choose an asset'),
              validateTrigger: 'onFinish',
              type: 'object',
            },
            {
              validator: () => {
                if (form.getFieldValue('assetData')?.assetTypeName !== ASSET_TYPE_SITE) {
                  return Promise.resolve();
                }

                if (canTargetSite) {
                  return Promise.resolve();
                }

                return Promise.reject(
                  new Error(
                    t(
                      'inspection.cannotCreateTargetingSites',
                      'Inspections cannot be created targeting sites. Please choose a more specific asset.'
                    )
                  )
                );
              },
              validateTrigger: 'onFinish',
            },
          ]}
        >
          {isInspectionTask ? (
            <TaskAssetPicker taskId={task.id} onChange={onAssetChange} disableAll={true} />
          ) : (
            <AssetPicker
              disableSubAssetUntilAssetTypeIsSelected={true}
              filterAssetTypeOption={filterAssetTypeOption}
              onChange={onAssetChange}
              onChangeAssetType={onAssetTypeChange}
              shouldIncludeAssetTypeSelector={INSPECTION_UPLOAD || WORK_ORDERS}
              initialAsset={asset}
              value={convertToAssetHierarchyItem(asset)}
              disableAll={!!asset}
              allowClear={true}
              getPopupContainer={triggerNode => triggerNode.parentElement!}
            />
          )}
        </Form.Item>
        <Form.Item
          label={t('inspection.inspectionDate', 'Inspection Date')}
          name="inspectionDate"
          rules={[
            {
              required: true,
              message: t('inspection.chooseInspectionDate', 'Please choose an inspection date'),
            },
          ]}
        >
          <UTCDatePicker data-testid="inspection-upload-date-input" placeholder={'YYYY-MM-DD'} />
        </Form.Item>
        <Form.Item
          label={t('table.description', 'Description')}
          name="description"
          {...(isInspectionTask
            ? {
                initialValue: t('inspection.taskNumberInspection', 'Task #{{number}} Inspection', {
                  number: task?.number,
                }),
              }
            : {})}
        >
          <Input
            data-testid="inspection-upload-description-input"
            placeholder={t('inspection.addADescription', 'Add a description')}
            disabled={isInspectionTask}
          />
        </Form.Item>
        <Form.Item
          label={t('table.type', 'Type')}
          name="typeId"
          rules={[
            {
              required: true,
              message: t('inspection.chooseInspectionType', 'Please choose an inspection type'),
            },
          ]}
        >
          <InspectionTypeChooser
            key={[
              currentAsset.assetTypeId,
              currentAsset.siteType,
              shouldUseBackendSiteInspectionTypes,
            ].join(':')}
            placeholder={t('inspection.chooseAnInspectionType', 'Choose an inspection type')}
            assetTypeId={currentAsset.assetTypeId}
            disabled={
              isCheckingSiteInspectionTypes ||
              isChoosingAssetType ||
              !currentAsset.assetTypeName ||
              (isSite && !canTargetSite)
            }
            onChange={onTypeChange}
            siteType={currentAsset.siteType}
            useBackendInspectionTypesOnly={shouldUseBackendSiteInspectionTypes}
            getPopupContainer={triggerNode => triggerNode.parentElement!}
          />
        </Form.Item>
        {hasVendorNames ? (
          <Form.Item
            label={t('table.vendor', 'Vendor')}
            name="vendorOrganizationId"
            rules={[
              { required: true, message: t('inspection.vendorRequired', 'Please select a Vendor') },
            ]}
          >
            <VendorOrgChooser
              relationshipType="delivery"
              includeCurrentOrg={true}
              getPopupContainer={triggerNode => triggerNode.parentElement!}
            />
          </Form.Item>
        ) : (
          <Form.Item
            label={t('table.vendor', 'Vendor')}
            name="vendorId"
            initialValue={getVendorId(process.env['DEPLOY_ENV'] !== 'prod')}
          >
            <InspectionVendorChooser
              disabled={vendorDisabled}
              typeId={inspectionTypeId}
              getPopupContainer={triggerNode => triggerNode.parentElement!}
            />
          </Form.Item>
        )}
        {!task && (
          <Form.Item label={t('table.task', 'Task')} name="selectedTaskId" rules={[]}>
            <InspectionTaskChooser
              assetId={
                currentAsset.assetId &&
                (currentAsset.assetTypeName !== ASSET_TYPE_SITE || canTargetSite)
                  ? currentAsset.assetId
                  : undefined
              }
              onChange={onTaskChange}
              form={form}
            />
          </Form.Item>
        )}
      </Form>
      <ModalActionButtons>
        <Button data-testid="inspection-upload-cancel-btn" onClick={onClose}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          data-testid="inspection-upload-save-btn"
          type="primary"
          loading={createTaskLoading || createInspectionLoading || isCheckingSiteInspectionTypes}
          onClick={form.submit}
        >
          {saveButtonText ?? t('common.save', 'Save')}
        </Button>
      </ModalActionButtons>
    </>
  );
};
